import {
	PriorityBadgeValueMapping,
	SourcingRequestStatusBadgeValueMapping,
} from '@supply-mover-2-0/shared';
import type { ListBuilderColumn } from '../../types';

/**
 * Request name and type column
 */
export const createRequestColumn = (): ListBuilderColumn => ({
	id: 'sr-name-type',
	fields: [
		{
			id: 'name-field',
			link: {
				type: 'object',
				enabled: true,
				objectTable: 'sourcing_request',
			},
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'name',
					objectTable: 'sourcing_request',
				},
			},
			styleConfig: {
				size: 'md',
				weight: 'medium',
				colorRule: { type: 'default' },
			},
			displayStyle: 'text',
		},
		{
			id: 'line-break-1',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: {
				size: 'md',
				colorRule: { type: 'default' },
			},
			displayStyle: 'line_break',
		},
		{
			id: 'type-field',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'type',
					objectTable: 'sourcing_request',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'theme',
					themeColor: 'textSecondary',
				},
			},
			displayStyle: 'badge',
			displayConfig: {
				valueMapping: [
					{ value: 'quote', displayValue: 'Quote', variant: 'info' },
					{ value: 'order', displayValue: 'Order', variant: 'success' },
				],
			},
		},
	],
	label: 'Request',
	headers: [{ id: 'h-name', text: 'Request' }],
	styleConfig: {
		size: 'md',
		colorRule: { type: 'default' },
	},
	sortable: true,
	sortFields: [
		{
			direction: 'ASC',
			fieldName: 'name',
			objectTable: 'sourcing_request',
		},
	],
	width: 'w-40',
});

/**
 * Status column
 */
export const createStatusColumn = (): ListBuilderColumn => ({
	id: 'sr-status',
	fields: [
		{
			id: 'status-field',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'status',
					objectTable: 'sourcing_request',
				},
			},
			displayStyle: 'badge',
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'default' },
				// BadgeDisplay reads valueMapping from styleConfig (the
				// previously-used `displayConfig.valueMapping` path is dead
				// — DisplayConfigConfigurator only handles entity-link config).
				valueMapping: [...SourcingRequestStatusBadgeValueMapping],
			},
		},
	],
	label: 'Status',
	headers: [{ id: 'h-status', text: 'Status' }],
	styleConfig: {
		size: 'md',
		colorRule: { type: 'default' },
	},
	sortable: true,
	sortFields: [
		{
			direction: 'ASC',
			fieldName: 'status',
			objectTable: 'sourcing_request',
		},
	],
	width: 'w-28',
});

/**
 * Priority column
 */
export const createPriorityColumn = (): ListBuilderColumn => ({
	id: 'sr-priority',
	fields: [
		{
			id: 'priority-field',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'priority',
					objectTable: 'sourcing_request',
				},
			},
			displayStyle: 'badge',
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'default' },
				valueMapping: [...PriorityBadgeValueMapping],
			},
		},
	],
	label: 'Priority',
	headers: [{ id: 'h-priority', text: 'Priority' }],
	styleConfig: {
		size: 'md',
		colorRule: { type: 'default' },
	},
	sortable: true,
	sortFields: [
		{
			direction: 'DESC',
			fieldName: 'priority',
			objectTable: 'sourcing_request',
		},
	],
	width: 'w-24',
});

/**
 * Client column
 */
export const createClientColumn = (): ListBuilderColumn => ({
	id: 'sr-client',
	fields: [
		{
			id: 'client-name-field',
			link: {
				type: 'url',
				enabled: true,
				urlPattern: '/clients/{fkClientId}',
			},
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'clientName',
					objectTable: 'sourcing_request',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'default' },
			},
			displayStyle: 'text',
		},
	],
	label: 'Client',
	headers: [{ id: 'h-client', text: 'Client' }],
	styleConfig: {
		size: 'md',
		colorRule: { type: 'default' },
	},
	sortable: true,
	sortFields: [
		{
			direction: 'ASC',
			fieldName: 'clientName',
			objectTable: 'sourcing_request',
		},
	],
	width: 'w-36',
});

/**
 * Lines column
 */
export const createLinesColumn = (): ListBuilderColumn => ({
	id: 'sr-lines',
	fields: [
		{
			id: 'lines-count-field',
			dataConfig: {
				type: 'template',
				template: '{linesFulfilled}/{totalLines}',
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'default' },
			},
			displayStyle: 'text',
		},
	],
	label: 'Lines',
	headers: [{ id: 'h-lines', text: 'Lines' }],
	styleConfig: {
		size: 'md',
		colorRule: { type: 'default' },
	},
	sortable: true,
	sortFields: [
		{
			direction: 'DESC',
			fieldName: 'total_lines',
			objectTable: 'sourcing_request',
		},
	],
	width: 'w-20',
});

/**
 * Assigned user column
 */
export const createAssignedColumn = (): ListBuilderColumn => ({
	id: 'sr-assigned',
	fields: [
		{
			id: 'assigned-user-field',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'assignedUserName',
					objectTable: 'sourcing_request',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'default' },
			},
			displayStyle: 'text',
			emptyValue: 'Unassigned',
		},
	],
	label: 'Assigned To',
	headers: [{ id: 'h-assigned', text: 'Assigned To' }],
	styleConfig: {
		size: 'md',
		colorRule: { type: 'default' },
	},
	sortable: true,
	sortFields: [
		{
			direction: 'ASC',
			fieldName: 'assignedUserName',
			objectTable: 'sourcing_request',
		},
	],
	width: 'w-28',
});

/**
 * Target date column
 */
export const createTargetDateColumn = (): ListBuilderColumn => ({
	id: 'sr-target-date',
	fields: [
		{
			id: 'target-date-field',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'targetDate',
					objectTable: 'sourcing_request',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'default' },
			},
			displayStyle: 'date',
		},
	],
	label: 'Target Date',
	headers: [{ id: 'h-target', text: 'Target Date' }],
	styleConfig: {
		size: 'md',
		colorRule: { type: 'default' },
	},
	sortable: true,
	sortFields: [
		{
			direction: 'ASC',
			fieldName: 'target_date',
			objectTable: 'sourcing_request',
		},
	],
	width: 'w-28',
});

/**
 * Created date column
 */
export const createCreatedDateColumn = (): ListBuilderColumn => ({
	id: 'sr-created-date',
	fields: [
		{
			id: 'created-date-field',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'createdDate',
					objectTable: 'sourcing_request',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'default' },
			},
			displayStyle: 'relative_date',
		},
	],
	label: 'Created',
	headers: [{ id: 'h-created', text: 'Created' }],
	styleConfig: {
		size: 'md',
		colorRule: { type: 'default' },
	},
	sortable: true,
	sortFields: [
		{
			direction: 'DESC',
			fieldName: '_created_date',
			objectTable: 'sourcing_request',
		},
	],
	width: 'w-28',
});

/**
 * Actions column
 */
export const createActionsColumn = (): ListBuilderColumn => ({
	id: 'sr-actions',
	fields: [
		{
			id: 'quick-view-action',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: {
				size: 'sm',
				iconName: 'eye',
				variant: 'ghost',
				isCircular: true,
				colorRule: {
					type: 'theme',
					themeColor: 'secondary',
				},
			},
			displayStyle: 'button',
			link: {
				enabled: true,
				type: 'callback',
				callbackName: 'quickView',
			},
		},
		{
			id: 'edit-action',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: {
				size: 'sm',
				iconName: 'faPencil',
				variant: 'ghost',
				isCircular: true,
				colorRule: {
					type: 'theme',
					themeColor: 'secondary',
				},
			},
			displayStyle: 'button',
			link: {
				enabled: true,
				type: 'callback',
				callbackName: 'edit',
			},
		},
		{
			id: 'delete-action',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: {
				size: 'sm',
				iconName: 'faTrash',
				variant: 'ghost',
				isCircular: true,
				colorRule: {
					type: 'theme',
					themeColor: 'danger',
				},
			},
			displayStyle: 'button',
			link: {
				enabled: true,
				type: 'callback',
				callbackName: 'delete',
			},
		},
	],
	label: 'Actions',
	headers: [{ id: 'h-actions', text: '' }],
	styleConfig: {
		size: 'md',
		colorRule: { type: 'default' },
	},
	width: 'w-24',
});

/**
 * Estimated total cost column ({@link SourcingRequestEntity.totalEstimatedCost}).
 * Sums (line.estimatedCost * line.quantityRequested) for non-cancelled
 * lines. Stored on the SR header by `updateLineCounts` so this shows on
 * the table without an extra round trip per row. Renders blank when no
 * costs are entered yet (vs $0 which would imply a real $0 budget).
 */
export const createEstimatedCostColumn = (): ListBuilderColumn => ({
	id: 'sr-estimated-cost',
	fields: [
		{
			id: 'estimated-cost-field',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'totalEstimatedCost',
					objectTable: 'sourcing_request',
				},
				defaultValue: '—',
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'default' },
				formatOptions: {
					currency: 'USD',
				},
			},
			displayStyle: 'currency',
		},
	],
	label: 'Est. Cost',
	headers: [{ id: 'h-est-cost', text: 'Est. Cost' }],
	styleConfig: {
		size: 'md',
		colorRule: { type: 'default' },
	},
	sortable: true,
	sortFields: [
		{
			direction: 'DESC',
			fieldName: 'total_estimated_cost',
			objectTable: 'sourcing_request',
		},
	],
	width: 'w-28',
});

/**
 * Create all sourcing request columns
 */
export const createSourcingRequestColumns = (): Array<ListBuilderColumn> => [
	createRequestColumn(),
	createStatusColumn(),
	createPriorityColumn(),
	createClientColumn(),
	createLinesColumn(),
	createEstimatedCostColumn(),
	createAssignedColumn(),
	createTargetDateColumn(),
	createCreatedDateColumn(),
	createActionsColumn(),
];
